// src/shared/context/BrandContext.tsx
//
// Expone la marca activa (resuelta a partir de VITE_BRAND_NAME) y se encarga
// de cargar dinámicamente la hoja de estilos de marca (src/brands/<marca>.css)
// que define las variables --primary-* para los temas claro y oscuro.
//
// - ainovis.css se carga estáticamente desde main.tsx (fallback por defecto).
// - Para cualquier otra marca se inyecta aquí su CSS de forma dinámica; como se
//   inserta DESPUÉS en el <head>, prevalece por cascada sobre ainovis.css y
//   sobre index.css, respetando el selector [data-theme] de cada tema.
//
// El BrandProvider se monta una sola vez, lo más arriba posible del árbol
// (dentro de ThemeProvider en App.tsx), de forma que TODOS los componentes y
// páginas (incluyendo las de tipo auth como login, forgot-password, etc.)
// reciban los colores correctos sin depender de su ubicación dentro del router.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { config } from '../../config/env';
import {
  BRANDS,
  DEFAULT_BRAND_ID,
  getBrandConfig,
  type BrandConfig,
  type BrandId,
} from '../../config/brand';
import type { LogoType } from '../../config/brand-logos';
import { useTheme } from './ThemeContext';

interface BrandContextValue {
  /** id interno de la marca activa (p.ej. 'AINOVIS'). */
  brandId: BrandId;
  /** Configuración completa de la marca activa (nombre, logos, etc). */
  brand: BrandConfig;
  /** Lista ordenada de marcas registradas, útil para selectores/debug. */
  availableBrands: BrandConfig[];
  /**
   * Devuelve la URL del logo de tipo `type` para el tema `effectiveTheme` (o
   * el tema actual si se omite). Resuelve marca + tema automáticamente.
   */
  getLogo: (type: LogoType, theme?: 'light' | 'dark') => string;
  /**
   * Resuelve un asset servido por `public/` teniendo en cuenta la marca activa.
   *
   * - Marca por defecto (AINOVIS): devuelve el path tal cual (los assets están
   *   en la raíz de `public/`).
   * - Marcas alternativas (p.ej. ALMA): reescribe el path para que apunte a la
   *   subcarpeta de la marca (`/home-image-light.png` → `/alma/home-image-light.png`).
   *
   * Así los consumidores solo piden el asset "lógico" y la marca decide de
   * dónde se sirve. Úsalo SIEMPRE para imágenes de `public/` que deban tener
   * variante por marca (banner del dashboard, og:image, etc.).
   *
   * @param path path absolute dentro de `public/`, empezando con '/'.
   */
  getPublicAsset: (path: string) => string;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

// Subcarpeta de `public/` por marca. La marca por defecto (la que no aparece
// aquí) sirve sus assets estáticos desde la raíz de `public/`. El id se usa en
// minúsculas como nombre de carpeta. Si una marca necesita otro nombre, ajústalo.
const BRAND_PUBLIC_SUBFOLDER: Partial<Record<BrandId, string>> = {
  ALMA_MEDICAL: 'alma',
};

// Mapa estático de cargadores de CSS para las marcas NO por defecto.
// IMPORTANTE: las rutas deben ser string literals (sin variables ni
// @vite-ignore) para que Vite las analice estáticamente y genere el chunk CSS
// correspondiente. La marca por defecto (AINOVIS) no entra aquí porque su CSS
// se carga estáticamente en main.tsx.
// El tipo de módulo CSS varia entre setups de Vite, por eso usamos `unknown`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ALTERNATE_BRAND_STYLESHEETS: Partial<Record<BrandId, () => Promise<any>>> = {
  ALMA_MEDICAL: () => import('../../brands/alma-medical.css'),
};

export const BrandProvider = ({ children }: { children: ReactNode }) => {
  // config.BRAND ya viene normalizado y validado por resolveBrandId en env.ts,
  // por lo que aquí es siempre un BrandId válido dentro del registro.
  const brandId: BrandId = config.BRAND ?? DEFAULT_BRAND_ID;
  const brand = useMemo(() => getBrandConfig(brandId), [brandId]);
  const { effectiveTheme } = useTheme();
  const availableBrands = useMemo(
    () => (Object.keys(BRANDS) as BrandId[]).map((id) => BRANDS[id]),
    [],
  );

  // Resolver un logo dado tipo + tema (usa el tema efectivo por defecto).
  const getLogo = useCallback(
    (type: LogoType, theme: 'light' | 'dark' = effectiveTheme) =>
      brand.logos[type][theme],
    [brand, effectiveTheme],
  );

  // Resolver un asset de `public/` según la marca. La marca por defecto sirve
  // desde la raíz; las demás desde la subcarpeta definida en
  // BRAND_PUBLIC_SUBFOLDER (p.ej. ALMA_MEDICAL → 'alma').
  const getPublicAsset = useCallback(
    (path: string) => {
      const subfolder = BRAND_PUBLIC_SUBFOLDER[brandId];
      if (!subfolder) return path; // marca por defecto: raíz
      // Normalizamos el path (quitamos la barra inicial) y lo montamos bajo la
      // subcarpeta de la marca.
      const clean = path.replace(/^\/+/, '');
      return `/${subfolder}/${clean}`;
    },
    [brandId],
  );

  // Carga dinámica de la hoja de estilos de marca. Para la marca por defecto
  // (AINOVIS) no hacemos nada: su CSS ya se carga estáticamente en main.tsx.
  // Para el resto, import() inyecta un <link> al final del <head> de Vite,
  // que prevalece por cascada sobre ainovis.css e index.css.
  useEffect(() => {
    // Etiqueta el documento con la marca activa para permitir selectores CSS
    // puntuales si hiciera falta (p.ej. [data-brand="alma_medical"] { ... }).
    document.documentElement.setAttribute('data-brand', brandId.toLowerCase());

    const loader = ALTERNATE_BRAND_STYLESHEETS[brandId];
    if (!loader) {
      // Marca por defecto (o sin loader): su CSS ya está cargado estáticamente.
      return;
    }

    let active = true;
    loader()
      .then(() => {
        if (!active) return;
        // El <link> ya está inyectado por Vite; nada más que hacer.
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`[brand] No se pudo cargar el CSS de marca "${brandId}":`, err);
      });

    return () => {
      active = false;
    };
  }, [brandId]);

  // Document.title por marca.
  useEffect(() => {
    document.title = brand.documentTitle;
  }, [brand.documentTitle]);

  // Favicon por marca. Sustituimos el href del <link rel=icon> que ya define
  // index.html, o lo creamos si no existe. Usamos el favicon .ico de la marca
  // (AINOVIS: /favicon.ico; ALMA: /alma/favicon.ico), no el isologo PNG.
  useEffect(() => {
    const href = brand.logos.favicon;
    const link =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
      document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"]');
    if (link) {
      link.href = href;
    } else {
      const created = document.createElement('link');
      created.rel = 'icon';
      created.href = href;
      document.head.appendChild(created);
    }
  }, [brand]);

  const value = useMemo<BrandContextValue>(
    () => ({ brandId, brand, availableBrands, getLogo, getPublicAsset }),
    [brandId, brand, availableBrands, getLogo, getPublicAsset],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export const useBrand = (): BrandContextValue => {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    throw new Error('useBrand debe usarse dentro de un <BrandProvider>');
  }
  return ctx;
};
